import Usercentrics, {
  UI_LAYER,
  isDefaultLabels,
  type BaseCategory,
  type DefaultLabels,
  type UserDecision,
} from "@usercentrics/cmp-browser-sdk";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  type ConsentBannerAction,
  readStoredGcmConsent,
  trackConsentBannerResolved,
} from "@dfp/react-web-analytics";

export type CmpStatus = "loading" | "ready" | "error";

export type UsercentricsCmp = {
  status: CmpStatus;
  /** True when the first layer must be shown because no decision was collected yet. */
  shouldCollectConsent: boolean;
  categories: BaseCategory[];
  /** Configured labels for the default (non-TCF) UI; null for other variants. */
  labels: DefaultLabels | null;
  acceptAll: () => Promise<void>;
  /** Denies every non-essential service; essential services are never denied. */
  denyNonEssential: () => Promise<void>;
  save: (decisions: UserDecision[]) => Promise<void>;
};

/**
 * Wraps the headless Usercentrics Browser SDK: consent state, accept/deny/save actions,
 * and app-language sync. Re-reads the SDK after every mutation.
 */
export function useUsercentricsCmp(
  settingsId: string,
  language?: string,
): UsercentricsCmp {
  const ucRef = useRef<Usercentrics | null>(null);
  const appliedLanguageRef = useRef<string | undefined>(undefined);

  const [status, setStatus] = useState<CmpStatus>("loading");
  const [shouldCollectConsent, setShouldCollectConsent] = useState(false);
  const [categories, setCategories] = useState<BaseCategory[]>([]);
  const [labels, setLabels] = useState<DefaultLabels | null>(null);

  const refreshFromSdk = useCallback((uc: Usercentrics) => {
    setCategories(uc.getCategoriesBaseInfo());
    const settingsLabels = uc.getSettingsLabels();
    setLabels(isDefaultLabels(settingsLabels) ? settingsLabels : null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const uc = new Usercentrics(settingsId);
    ucRef.current = uc;

    uc.init()
      .then((initial) => {
        if (cancelled) {
          return;
        }
        setShouldCollectConsent(initial.initialLayer === UI_LAYER.FIRST_LAYER);
        refreshFromSdk(uc);
        appliedLanguageRef.current = uc.getSettingsCore().language.selected;
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [settingsId, refreshFromSdk]);

  useEffect(() => {
    const uc = ucRef.current;
    if (
      status !== "ready" ||
      !uc ||
      !language ||
      appliedLanguageRef.current === language
    ) {
      return;
    }

    let cancelled = false;
    uc.changeLanguage(language)
      .then(() => {
        if (!cancelled) {
          appliedLanguageRef.current = language;
          refreshFromSdk(uc);
        }
      })
      .catch(() => {
        // A failed language switch keeps the previously loaded labels; not fatal.
      });

    return () => {
      cancelled = true;
    };
  }, [language, status, refreshFromSdk]);

  const runAndRefresh = useCallback(
    async (
      action: ConsentBannerAction,
      op: (uc: Usercentrics) => Promise<unknown>,
    ) => {
      const uc = ucRef.current;
      if (!uc) {
        return;
      }
      await op(uc);
      refreshFromSdk(uc);
      const gcm = readStoredGcmConsent();
      if (gcm) {
        trackConsentBannerResolved({ action, gcm });
      }
    },
    [refreshFromSdk],
  );

  const acceptAll = useCallback(
    () => runAndRefresh("accept-all", (uc) => uc.acceptAllServices()),
    [runAndRefresh],
  );

  const denyNonEssential = useCallback(
    () => runAndRefresh("reject-all", (uc) => uc.denyAllServices()),
    [runAndRefresh],
  );

  const save = useCallback(
    (decisions: UserDecision[]) =>
      runAndRefresh("save", (uc) => uc.updateServices(decisions)),
    [runAndRefresh],
  );

  return {
    status,
    shouldCollectConsent,
    categories,
    labels,
    acceptAll,
    denyNonEssential,
    save,
  };
}
